import { type RefObject, useCallback, useLayoutEffect } from 'react';

import {
  APP_HEADER_HEIGHT_VAR,
  APP_SIDE_PANEL_WIDTH_VAR,
  APP_SIDEBAR_WIDTH_VAR,
} from '@flipdish/ui-library/components/organisms/SlideoutMenu';
import { useResizeObserver } from '@flipdish/ui-library/hooks/useResizeObserver';

/**
 * The CSS custom properties a host sets so an MFE's `SlideoutMenu` covers the page area only
 * (ui-library SlideoutMenu docs, "Placement"). serverless-app-template's
 * `PortalMock/hooks/useHostChromeVars.ts` mirrors this for local MFE work, so change both together.
 */
export const HOST_CHROME_VARS = {
  headerHeight: APP_HEADER_HEIGHT_VAR,
  sidebarWidth: APP_SIDEBAR_WIDTH_VAR,
  sidePanelWidth: APP_SIDE_PANEL_WIDTH_VAR,
} as const;

/** Distances from the viewport edges to the page area, as the host writes them. */
export type HostChromeInsets = {
  headerHeight: number;
  sidebarWidth: number;
  sidePanelWidth: number;
};

/**
 * Reads the page area's box as the three insets. Measured, not configured, because the rail
 * collapses, the side panel docks and the header changes height below `lg`.
 *
 * @param viewportWidth The width fixed insets resolve against (scrollbar excluded), to turn the
 *   right edge into a right inset.
 * @returns Whole pixel insets, never negative.
 */
export const toHostChromeInsets = (
  rect: Pick<DOMRectReadOnly, 'top' | 'left' | 'right'>,
  viewportWidth: number
): HostChromeInsets => ({
  headerHeight: Math.max(0, Math.round(rect.top)),
  sidebarWidth: Math.max(0, Math.round(rect.left)),
  sidePanelWidth: Math.max(0, Math.round(viewportWidth - rect.right)),
});

/**
 * Publishes the page area's insets as the host chrome variables on `document.body`. An MFE's
 * overlay anchor is a child of `body` (UIProvider with `scopeId`), so it inherits them, and a
 * `SlideoutMenu` rendered into it is clipped to the page area: under the header, right of the
 * rail, left of a docked side panel. A side panel that covers the page below `lg` leaves the page
 * area's box unchanged, so the width stays `0px`. The slideout closes on that panel's
 * `fd-page-cover` event instead, so the two never show together.
 *
 * @param pageAreaRef The scroll container the MFE mounts in.
 */
export const useHostChromeVars = (pageAreaRef: RefObject<HTMLElement | null>): void => {
  const publish = useCallback(() => {
    const pageArea = pageAreaRef.current;
    if (!pageArea) return;
    const insets = toHostChromeInsets(
      pageArea.getBoundingClientRect(),
      document.documentElement.clientWidth
    );
    const { style } = document.body;
    style.setProperty(HOST_CHROME_VARS.headerHeight, `${insets.headerHeight}px`);
    style.setProperty(HOST_CHROME_VARS.sidebarWidth, `${insets.sidebarWidth}px`);
    style.setProperty(HOST_CHROME_VARS.sidePanelWidth, `${insets.sidePanelWidth}px`);
  }, [pageAreaRef]);

  // Layout effect both ways: set before paint, and clear before the next layout's page area sets.
  useLayoutEffect(() => {
    publish();
    return () => {
      const { style } = document.body;
      for (const name of Object.values(HOST_CHROME_VARS)) {
        style.removeProperty(name);
      }
    };
  }, [publish]);
  // Every chrome change (rail, panel, header, help drawer) also resizes the page area.
  useResizeObserver({ ref: pageAreaRef, onResize: publish });
};
